import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import CharacterClassSelect from 'app/frontend/base/components/CharacterClassSelect'
import CharacterSpecSelect, {
  parseRoleOrSpec,
} from 'app/frontend/base/components/CharacterSpecSelect'
import CharacterClass from 'app/frontend/types/custom/CharacterClass'
import CharacterRole from 'app/frontend/types/custom/CharacterRole'
import { cn } from '@/lib/utils'

export type ManualCharacterValues = {
  name: string
  surname: string
  characterClass: CharacterClass
  specId?: number
  role?: CharacterRole
}

type Props = {
  onSubmit: (values: ManualCharacterValues, reset: () => void) => void
  isPending?: boolean
  onCancel?: () => void
  submitLabel: string
  inModal?: boolean
}

const ManualCharacterForm = ({
  onSubmit,
  isPending,
  onCancel,
  submitLabel,
  inModal,
}: Props) => {
  const [name, setName] = useState('')
  const [surname, setSurname] = useState('')
  const [characterClass, setCharacterClass] = useState<CharacterClass>()
  const [roleOrSpec, setRoleOrSpec] = useState<string>()

  const reset = () => {
    setName('')
    setSurname('')
    setCharacterClass(undefined)
    setRoleOrSpec(undefined)
  }

  const canSubmit =
    name.trim().length > 1 &&
    surname.trim().length > 1 &&
    !!characterClass &&
    !!roleOrSpec

  return (
    <form
      className='flex flex-col gap-2'
      onSubmit={(event) => {
        event.preventDefault()
        if (!canSubmit || !characterClass || !roleOrSpec) return

        onSubmit(
          {
            name: name.trim(),
            surname: surname.trim(),
            characterClass,
            ...parseRoleOrSpec(roleOrSpec),
          },
          reset,
        )
      }}
    >
      <div
        className={cn(
          'grid gap-2',
          inModal ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
        )}
      >
        <Input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder='Name'
          maxLength={12}
          autoFocus={inModal}
        />
        <Input
          value={surname}
          onChange={(event) => setSurname(event.target.value)}
          placeholder='Surname'
          maxLength={12}
        />
        <CharacterClassSelect
          value={characterClass}
          setValue={(value) => {
            setCharacterClass(value)
            setRoleOrSpec(undefined)
          }}
          inModal={inModal}
        />
        <CharacterSpecSelect
          characterClass={characterClass}
          value={roleOrSpec}
          setValue={setRoleOrSpec}
          includeRoles
          inModal={inModal}
        />
      </div>

      <div className='flex justify-end gap-2'>
        {onCancel && (
          <Button type='button' variant='ghost' size='sm' onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          type='submit'
          size='sm'
          disabled={!canSubmit}
          loading={isPending}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}

export default ManualCharacterForm
